/**
 * Nosto-HeaderRecommendations.js
 *
 * Recomendaciones iniciales del buscador del header.
 * Usa exclusivamente el ProductCard universal.
 */
(function initializeNostoHeaderRecommendations(global) {
    "use strict";

    const CARD_SELECTOR = "[data-product-card]";

    /* placementId -> Promise<detail> */
    const recommendationRequests = new Map();

    class NostoHeaderRecommendations {
        constructor(root) {
            this.root = root;
            this.container = root.querySelector(
                "[data-header-search-recommendations-products]"
            );
            this.recommendations = root.querySelector(
                "[data-header-search-recommendations]"
            );

            this.viewService = null;
            this.hasLoaded = false;
            this.isLoading = false;
            this.details = null;

            this.handleSearchOpen = this.handleSearchOpen.bind(this);
        }

        init() {
            if (!this.container || !this.recommendations) {
                return;
            }

            this.initializeViewService();
            this.details = this.root.closest("details");

            if (!this.details) {
                return;
            }

            this.details.addEventListener("toggle", this.handleSearchOpen);

            if (this.details.open) {
                this.load();
            }
        }

        initializeViewService() {
            const shared = global.NOSTO_SHARED_PRODUCT_VIEW_SERVICE;

            if (shared && typeof shared.getView === "function") {
                this.viewService = shared;
                return;
            }

            if (typeof global.NostoProductViewService !== "function") {
                console.error(
                    "[NostoHeaderRecommendations] NostoProductViewService no está disponible."
                );
                return;
            }

            this.viewService = new global.NostoProductViewService({
                cardView: "product-card",
                quickBuyView: "nosto-quick-buy",
                concurrency: 4,
                cache: true,
                debug: false,
            });

            global.NOSTO_SHARED_PRODUCT_VIEW_SERVICE = this.viewService;
        }

        handleSearchOpen() {
            if (!this.details?.open) return;
            this.load();
        }

        async load() {
            if (this.hasLoaded || this.isLoading) {
                return;
            }

            if (
                !this.viewService ||
                typeof this.recommendations.load !== "function"
            ) {
                return;
            }

            this.isLoading = true;

            const placementId = String(
                this.recommendations.dataset.placementId || ""
            ).trim();

            if (!placementId) {
                this.renderEmpty();
                this.isLoading = false;
                return;
            }

            try {
                let request = recommendationRequests.get(placementId);

                if (!request) {
                    request = this.recommendations.load();
                    recommendationRequests.set(placementId, request);
                }

                const detail = await request;

                if (!detail) {
                    this.renderEmpty();
                    return;
                }

                const products = (
                    Array.isArray(detail.recommendedProducts)
                        ? detail.recommendedProducts
                        : Array.isArray(detail.allRecommendedProducts)
                            ? detail.allRecommendedProducts
                            : []
                ).slice(0, 4);

                if (!products.length) {
                    this.renderEmpty();
                    return;
                }

                await this.renderProducts(products, {
                    detail,
                    placementId,
                });

                this.hasLoaded = true;
            } catch (error) {
                console.error("[NostoHeaderRecommendations]", error);
                this.renderEmpty();
            } finally {
                this.isLoading = false;
            }
        }

        async renderProducts(
            products,
            {
                detail = {},
                placementId = "",
            } = {}
        ) {
            this.container.replaceChildren();

            const resultId = String(
                detail.resultId ||
                detail.rawRecommendation?.result_id ||
                ""
            ).trim();

            const divId = String(
                detail.divId ||
                detail.rawRecommendation?.div_id ||
                ""
            ).trim();

            const renderPromises = products.map(
                async (recommendedProduct) => {
                    const productId = String(
                        recommendedProduct.productId ||
                        recommendedProduct.id ||
                        ""
                    ).trim();

                    const handle = this.getHandle(recommendedProduct);

                    if (!handle) {
                        console.warn(
                            "[NostoHeaderRecommendations] Producto sin handle.",
                            recommendedProduct
                        );
                        return null;
                    }

                    const attributionKey = String(
                        recommendedProduct.attributionKey ||
                        recommendedProduct.recoId ||
                        recommendedProduct.reco_id ||
                        resultId ||
                        ""
                    ).trim();

                    const placeholder = document.createElement("div");
                    placeholder.className = "search-empty-state__product";
                    placeholder.dataset.productId = productId;
                    placeholder.dataset.productSource = "nosto-recommendation";
                    placeholder.dataset.nostoPlacementId = placementId;
                    placeholder.dataset.nostoResultId = resultId;
                    placeholder.dataset.nostoDivId = divId;
                    placeholder.dataset.nostoAttributionKey = attributionKey;

                    this.container.appendChild(placeholder);

                    try {
                        const html = await this.viewService.getView({
                            handle,
                            productId,
                            view: "product-card",
                            validationSelector: CARD_SELECTOR,
                            context: {
                                source: "header-recommendations",
                                track: "recommendation",
                                attributionKey,
                                placementId,
                                resultId,
                                divId,
                            },
                        });

                        const template = document.createElement("template");
                        template.innerHTML = String(html).trim();

                        const sourceCard = template.content.querySelector(
                            CARD_SELECTOR
                        );

                        if (!sourceCard) {
                            throw new Error(
                                "La vista product-card no contiene [data-product-card]."
                            );
                        }

                        const card = sourceCard.cloneNode(true);

                        card.dataset.productSource = "nosto-recommendation";
                        card.dataset.nostoTrack = "recommendation";
                        card.dataset.nostoOriginalProductId = productId;
                        card.dataset.nostoPlacementId = placementId;
                        card.dataset.nostoResultId = resultId;
                        card.dataset.nostoDivId = divId;
                        card.dataset.nostoAttributionKey = attributionKey;

                        placeholder.replaceChildren(card);
                        global.initializeProductCards?.(placeholder);

                        return placeholder;
                    } catch (error) {
                        console.error(
                            "[NostoHeaderRecommendations] Error cargando card.",
                            {
                                productId,
                                handle,
                                error,
                            }
                        );

                        placeholder.remove();
                        return null;
                    }
                }
            );

            await Promise.allSettled(renderPromises);

            global.refreshProductCards?.(this.container);
            this.container.dataset.loaded = "true";
        }

        getHandle(product) {
            const directHandle = String(
                product.handle ||
                product.productHandle ||
                ""
            ).trim();

            if (directHandle) return directHandle;

            const url = String(product.url || "").trim();
            if (!url) return "";

            try {
                const parsed = new URL(url, global.location.origin);
                const parts = parsed.pathname.split("/").filter(Boolean);
                const index = parts.indexOf("products");

                return index >= 0
                    ? parts[index + 1] || ""
                    : "";
            } catch (error) {
                return "";
            }
        }

        renderEmpty() {
            this.container?.replaceChildren();

            if (this.container) {
                this.container.dataset.empty = "true";
            }
        }
    }

    function initialize() {
        document
            .querySelectorAll("[data-header-search-recommendations-root]")
            .forEach((root) => {
                if (
                    root.dataset.headerSearchRecommendationsInitialized ===
                    "true"
                ) {
                    return;
                }

                root.dataset.headerSearchRecommendationsInitialized = "true";

                const instance = new NostoHeaderRecommendations(root);
                instance.init();
                root.__nostoHeaderRecommendationsInstance = instance;
            });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initialize, {
            once: true,
        });
    } else {
        initialize();
    }

    document.addEventListener("shopify:section:load", initialize);
    global.NostoHeaderRecommendations = NostoHeaderRecommendations;
})(window);